import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  FormHelperText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadBalancerAPI } from '../../api/load-balancer';
import { useRecoilState } from 'recoil';
import { authState, userStorageKey } from '../../api/state';
import LoadingButton from '@mui/lab/LoadingButton';

interface TwoFAStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateNewDevice?: () => void;
  isClickMenu: boolean;
  isOpenRecoveryCodeDialog?: () => void;
}

const TwoFAStateModal = ({
  isOpen,
  onClose,
  isClickMenu,
  onCreateNewDevice,
  isOpenRecoveryCodeDialog,
}: TwoFAStateModalProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [timer, setTimer] = useState(0);
  const [getCodeCount, setGetCodeCount] = useState(0);
  const [isGetVerificationCodeLoading, setIsGetVerificationCodeLoading] = useState(false);
  const navigate = useNavigate();
  const [twoFAState, setTwoFAState] = useState<boolean | null>(null);
  const location = useLocation();
  const { redirectTo } = location.state || { redirectTo: '/' };
  const [authStateValue, setAuthState] = useRecoilState(authState);

  useEffect(() => {
    if (authStateValue?.user?.id) {
      get2FAStatus();
    }
  }, [authStateValue?.user?.twoFAState, authStateValue?.user?.id]);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer]);

  const get2FAStatus = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.getTwoFAState(authStateValue?.user?.id);
      if (response.data) {
        setTwoFAState(response.data.twoFAState);
      }
    } catch {
      console.error('Failed to get 2FA state');
    }
  };

  const handleGetCode = async () => {
    if (timer > 0) return;

    const trimmedEmail = authStateValue?.user?.email.trim();
    if (!trimmedEmail) {
      setErrorMessage('Invalid Email');
      return;
    }

    try {
      setIsGetVerificationCodeLoading(true);
      await loadBalancerAPI.apiInstance.getVerificationCode(trimmedEmail);
      setTimer(120);
      setGetCodeCount((prev) => prev + 1);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Unable to get authentication code');
    } finally {
      setIsGetVerificationCodeLoading(false);
    }
  };

  const validateVerificationCode = async () => {
    if (!authStateValue?.user?.email) return;
    try {
      if (verificationCode.length !== 6) {
        setErrorMessage('The authentication code must be 6 characters');
        return false;
      }
      await loadBalancerAPI.apiInstance.verifyEmailCode(authStateValue?.user?.email, verificationCode);
      setErrorMessage('');
      return true;
    } catch {
      setErrorMessage('Invalid authentication code');
      return false;
    }
  };

  const handleChange2FAState = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const isValidCode = await validateVerificationCode();
      if (!isValidCode) return;

      const state = !twoFAState;
      const response = await loadBalancerAPI.apiInstance.change2FAState(authStateValue?.user?.id, state);
      if (response.status === 200) {
        const savedValue = localStorage.getItem(userStorageKey);
        if (savedValue) {
          try {
            const parsedValue = JSON.parse(savedValue);
            if (parsedValue?.refreshToken) {
              const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
              setAuthState(data.data);
            }
          } catch (error) {
            console.error('Error refreshing token', error);
          }
        }
        setTwoFAState(state);
        if (location.pathname.includes('analytics') || location.pathname.includes('content-management')) {
          navigate('/home');
        }
        if (state === true) {
          if (onCreateNewDevice) {
            onCreateNewDevice();
          }
        } else if (!state) {
          await loadBalancerAPI.apiInstance.deleteTwoFA(authStateValue?.user?.id);
        }
        onClose();
      }
    } catch {
      console.error('Failed to change 2FA state');
    }
  };

  const handleEnable2FAState = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const isValidCode = await validateVerificationCode();
      if (!isValidCode) return;

      const response = await loadBalancerAPI.apiInstance.change2FAState(authStateValue?.user?.id, true);
      if (response.status === 200) {
        const savedValue = localStorage.getItem(userStorageKey);
        if (savedValue) {
          try {
            const parsedValue = JSON.parse(savedValue);
            if (parsedValue?.refreshToken) {
              const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
              setAuthState(data.data);
            }
          } catch (error) {
            console.error('Error refreshing token', error);
          }
        }
        setTwoFAState(true);
        if (onCreateNewDevice) {
          onCreateNewDevice();
        }
      }
    } catch {
      console.error('Failed to change 2FA state');
    }
  };

  const handleAccessToAdminPage = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.change2FAState(authStateValue?.user?.id, false);
      if (response.status === 200) {
        const savedValue = localStorage.getItem(userStorageKey);
        if (savedValue) {
          try {
            const parsedValue = JSON.parse(savedValue);
            if (parsedValue?.refreshToken) {
              const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
              setAuthState(data.data);
            }
          } catch (error) {
            console.error('Error refreshing token', error);
          }
        }
        navigate(redirectTo);
        setTwoFAState(false);
      }
    } catch {
      console.error('Failed to change 2FA state');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (isClickMenu) {
        handleChange2FAState();
      } else if (!isClickMenu) {
        handleEnable2FAState();
    }
  };
}

  const handleCancel = () => {
    setVerificationCode('');
    setErrorMessage('');
    onClose();
    if (location.pathname.includes('two-factor-auth')) {
      navigate('/home');
    }
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs">
      <img src="google-authenticator.jpg" alt="authenticator-icon" style={{ width: '100%', maxWidth: '100vw' }} />
      <DialogTitle>Two-Factor Authentication</DialogTitle>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <DialogContent style={{ padding: '10px' }}>
          <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <TextField
              required
              margin="dense"
              label="Email verification code"
              type="text"
              fullWidth
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              error={!!errorMessage}
              helperText={errorMessage}
              onKeyDown={handleKeyDown}
              style={{ marginBottom: '8px' }}
            />
            <LoadingButton
              variant="contained"
              color="info"
              size="small"
              disabled={timer > 0}
              loading={isGetVerificationCodeLoading}
              onClick={handleGetCode}
              sx={{ minWidth: '83px', padding: '0px', height: '50px' }}
            >
              {getCodeCount > 0 ? 'Resend' : 'Get Code'}
            </LoadingButton>
          </Stack>
          {timer > 0 && <FormHelperText>Didn't receive a code? {timer} seconds.</FormHelperText>}
        </DialogContent>
      </div>
      <DialogActions style={{ flexDirection: 'column' }}>
        {isClickMenu && (
          <Button
            variant="contained"
            onClick={handleChange2FAState}
            color="primary"
            disabled={verificationCode.length !== 6}
            sx={{
              marginBottom: '10px',
              '&:disabled': {
                color: '#ffffff',
                backgroundColor: '#bdbdbd',
              },
            }}
          >
            {twoFAState === null || twoFAState === false ? `Enable ` : `Disable `} 2FA authenticator
          </Button>
        )}
        {!isClickMenu && (
          <Button
            variant="contained"
            onClick={handleEnable2FAState}
            color="primary"
            disabled={verificationCode.length !== 6}
            sx={{
              marginBottom: '10px',
              '&:disabled': {
                color: '#ffffff',
                backgroundColor: '#bdbdbd',
              },
            }}
          >
            Enable 2FA authenticator
          </Button>
        )}
        {!isClickMenu && (
          <Button variant="contained" onClick={handleAccessToAdminPage} color="primary" sx={{ marginBottom: '10px' }}>
            Do not enable 2FA authenticator
          </Button>
        )}
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFAStateModal;
